/**
 * Resolve the DOM `<table>` for the table containing the selection.
 *
 * Shared because both table menus must anchor to the same element. Anchoring
 * either of them to the SELECTION instead makes the surface chase the caret
 * from cell to cell — it then covers the line being typed, and (placed above)
 * the line above that. No mature editor anchors table chrome to the caret; the
 * anchor is always stable geometry.
 */

import { CellSelection, TableMap } from '@tiptap/pm/tables'
import type { Editor } from '@tiptap/react'

export function findActiveTableElement(editor: Editor): HTMLElement | null {
  const $from = editor.state.selection.$from
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type.name !== 'table') continue
    const dom = editor.view.nodeDOM($from.before(depth))
    if (!(dom instanceof HTMLElement)) return null
    // With column resizing on, the node view's own element is the scroll
    // WRAPPER, so `nodeDOM` hands back the div rather than the `<table>`.
    // Anchoring to the wrapper drifts away from a table narrower than the page.
    return dom.querySelector('table') ?? dom
  }
  return null
}

/**
 * Where a table menu is parked when it must not be seen.
 *
 * Far enough off screen that Popper's own `hide` modifier also flags the
 * reference as hidden, which the CSS in `global.css` uses to take the menu out
 * of the tab order as well as out of sight.
 */
const OFFSCREEN = new DOMRect(-10000, -10000, 0, 0)

/** The nearest scrolling ancestor of the editor, if it sits inside one. */
function editorScrollParent(editor: Editor): HTMLElement | null {
  let el = editor.view.dom.parentElement
  while (el) {
    const overflowY = getComputedStyle(el).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') return el
    el = el.parentElement
  }
  return null
}

/**
 * Room a menu needs above or below its anchor. Both table menus are a single
 * row of 28px controls in an 8px-padded box, plus tippy's own 8px offset.
 */
const MENU_CLEARANCE_PX = 56

/**
 * The region a table menu may occupy: the editor's own box, clipped to
 * whatever of it is currently scrolled into view.
 *
 * Both halves matter. The SCROLL PARENT alone is too generous — in the card
 * detail modal that is the whole modal body, so a menu could satisfy it while
 * sitting on the Attachments section below the description. The EDITOR BOX
 * alone ignores scrolling, so a menu could satisfy it while sitting outside
 * the visible part of a long description.
 */
function menuBounds(editor: Editor): { top: number; bottom: number } {
  const editorRect = editor.view.dom.getBoundingClientRect()
  const scrollport = editorScrollParent(editor)
  if (!scrollport) return { top: editorRect.top, bottom: editorRect.bottom }
  const clip = scrollport.getBoundingClientRect()
  return {
    top: Math.max(editorRect.top, clip.top),
    bottom: Math.min(editorRect.bottom, clip.bottom),
  }
}

/**
 * The rect a table menu anchors to — the midpoint of the table's top or
 * bottom edge — or a parking spot off screen when a menu there would fall
 * outside the editor.
 *
 * **The bounds check lives HERE, and that is the whole point.** It was in
 * `shouldShow` first, and that does not work: TipTap's bubble-menu plugin
 * re-runs `shouldShow` only from a ProseMirror view update, and it early
 * returns unless the selection or the document changed. Scrolling is neither.
 * Popper, meanwhile, calls `getReferenceClientRect` on every reposition,
 * including every scroll frame — so the menu faithfully tracked the table out
 * of the description and over the Attachments section below it while the
 * visibility guard was never consulted again.
 *
 * Anything that decides whether one of these menus may be SEEN has to be
 * reachable from this function, not from `shouldShow`.
 */
export function tableMenuReferenceRect(editor: Editor, edge: 'top' | 'bottom'): DOMRect {
  const table = findActiveTableElement(editor)
  if (!table) return OFFSCREEN
  const rect = table.getBoundingClientRect()
  const bounds = menuBounds(editor)
  const anchor = edge === 'top' ? rect.top : rect.bottom
  if (anchor < bounds.top || anchor > bounds.bottom) return OFFSCREEN
  const fits =
    edge === 'top'
      ? anchor - MENU_CLEARANCE_PX >= bounds.top
      : anchor + MENU_CLEARANCE_PX <= bounds.bottom
  if (!fits) return OFFSCREEN
  return new DOMRect(rect.left + rect.width / 2, anchor, 0, 0)
}

/**
 * Cheap companion gate for `shouldShow`, which runs at selection time. It
 * cannot replace the check in `tableMenuReferenceRect` — see the note there —
 * but it keeps a menu from appearing at all when the table is already out of
 * view when the caret lands in it.
 */
export function isTableEdgeInView(editor: Editor, edge: 'top' | 'bottom'): boolean {
  return tableMenuReferenceRect(editor, edge) !== OFFSCREEN
}

/**
 * Popper options shared by both table menus.
 *
 * `boundary` is the editor element explicitly: Popper's default is derived
 * from the reference ELEMENT, and these menus deliberately use a synthetic
 * rect, so without this both flip and preventOverflow are computed against the
 * viewport. `tether: false` matters just as much — Popper keeps a popper glued
 * to its reference even outside the boundary unless you turn it off, which is
 * exactly how a bounded menu still escaped.
 */
export function tableMenuPopperOptions(editor: Editor) {
  const boundary = editor.view.dom
  return {
    modifiers: [
      { name: 'flip', options: { boundary, fallbackPlacements: [] } },
      { name: 'preventOverflow', options: { boundary, padding: 4, tether: false } },
      { name: 'hide', enabled: true },
    ],
  }
}

/**
 * Put the whole table on the clipboard.
 *
 * Deliberately routed through the BROWSER's copy, not a hand-rolled write:
 * selecting every cell and firing a copy makes ProseMirror serialize the
 * selection through the very same path a manual Cmd+C uses, so the clipboard
 * gets both halves — real `<table>` markup for a rich destination and our GFM
 * projection for a plain-text one — with no second implementation to keep in
 * step with `table-markdown.ts`.
 *
 * A full-table `CellSelection` is what produces the table NODE rather than a
 * bare run of rows (prosemirror-tables returns the table itself when the
 * selection spans every row and column), which is why the whole table is
 * selected rather than its first and last cell.
 *
 * The original selection is restored afterwards: a multi-cell selection hides
 * the footer menu, so leaving it in place would make the menu vanish the
 * instant you used it.
 */
export function copyTableToClipboard(editor: Editor): boolean {
  const { state } = editor
  const $from = state.selection.$from
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth)
    if (node.type.name !== 'table') continue
    const map = TableMap.get(node)
    const first = map.map[0]
    const last = map.map[map.map.length - 1]
    if (first === undefined || last === undefined) return false
    const tableStart = $from.start(depth)
    const previous = state.selection
    const selection = CellSelection.create(state.doc, tableStart + first, tableStart + last)
    editor.view.dispatch(state.tr.setSelection(selection))
    editor.view.focus()
    let copied = false
    try {
      copied = document.execCommand('copy')
    } catch {
      copied = false
    }
    editor.view.dispatch(
      editor.state.tr.setSelection(previous.map(editor.state.doc, editor.state.tr.mapping)),
    )
    return copied
  }
  return false
}
